import type { SearchArea, SortKey, SourcePostFilters } from './types.ts';
import { maMiekkieFiltry, predykatyWykluczenia } from './predykatyMiekkie.ts';
import { RODZAJE_BEZ_AGENCJI } from './bezAgencji.ts';

/**
 * ŁAŃCUCH FILTRÓW I SORTOWANIE SERP-u — jedno miejsce dla aplikacji i funkcji `send-filter-alerts`
 * (plan §1.68, docs/AlertEmails.md §2: „wybór i kolejność = strona wyników, z tego samego kodu”).
 * Przeniesione BEZ ZMIAN z `src/hooks/serpQuery.ts`, które eksportuje wszystko dalej.
 *
 * Czyste funkcje nad strukturalnym `SerpFilterableQuery` — żadnego importu supabase, więc logika
 * filtrów/sortowania/stronicowania jest testowalna nagrywającą atrapą (serpQuery.test.ts), a plik
 * jest zgodny z Deno (same importy względne z `.ts`).
 */

export const SERP_PAGE_SIZE = 48;

/** Identyfikator, którego nie ma żadna oferta — „Ulubione” przy pustej liście daje zero wyników. */
export const BRAK_ULUBIONYCH = '00000000-0000-0000-0000-000000000000';

/**
 * Dzisiejsza data kalendarzowa oglądającego jako `YYYY-MM-DD`.
 *
 * Bez `toISOString()`, i to nie jest czepialstwo: ta metoda przelicza na UTC, więc o 1:00 w
 * Warszawie zwraca WCZORAJ. Filtr „od zaraz" porównuje daty kalendarzowe, a przesunięcie o
 * dobę wyrzuca z wyników wszystko, co jest wolne od dzisiaj.
 */
export function dzisiejszaDataLokalna(teraz: Date = new Date()): string {
  const dwie = (n: number) => String(n).padStart(2, '0');
  return `${teraz.getFullYear()}-${dwie(teraz.getMonth() + 1)}-${dwie(teraz.getDate())}`;
}
/**
 * Ile pinów bierze mapa. TYSIĄC, BO TYLE DAJE SERWER — nie dlatego, że tyle nam wystarcza.
 *
 * Stało tu 5000 i była to liczba nieprawdziwa: PostgREST tnie odpowiedź na `db-max-rows`, a
 * u nas to 1000. Zmierzone 27.09: `offers?select=id&limit=2000` zwraca 1000 wierszy, a
 * nagłówek mówi `content-range: 0-999/7163`. Przy 3 928 ofertach w wyniku mapa dostaje więc
 * niecały tysiąc pinów i nikt tego nigdzie nie zgłaszał — od 27.09 mówi to wprost nota na
 * mapie („Na mapie 927 z 3 928 ofert").
 *
 * Podniesienie sufitu to zmiana konfiguracji Supabase (per środowisko), a nie kodu; dociąganie
 * pinów stronami albo zawężenie ich do bieżącego kadru to osobna decyzja — obie czekają na
 * Piotra. Do tego czasu ta stała ma mówić prawdę o tym, co się dzieje.
 */
export const PINS_LIMIT = 1000;

/**
 * Slim SERP select: the union of what SourcePostCard, the map popup and the
 * OfferPage instant-paint actually render (~30 of the serving layer's 59
 * columns). Deliberately excludes the columns the SERP never reads —
 * description, cluster_*, portal_*, contact_* (the last are not even granted).
 *
 * `alias:column` pairs keep the CLIENT-side field names (`extracted_*`) while
 * reading the serving layer's real ones. The names are a leftover from when the
 * front read `source_posts`; renaming them is a mechanical, zero-behaviour
 * follow-up (~280 call sites) deliberately kept out of the cutover. Note the
 * asymmetry this creates and don't "fix" it: PostgREST aliases rename only the
 * OUTPUT, so every filter/sort below must use the REAL column name (rent_pln,
 * not extracted_rent_pln).
 *
 * `photos` needs no client-side rebuild: flat.offers.photos is already
 * `photos_storage || portal_photos_storage`, i.e. durable-only by construction
 * ([P11]). The old withDurablePhotos() fallback to expiring raw FB URLs is gone
 * with it.
 */
export const SERP_COLUMNS =
  'id,source,source_url,posted_at,stale_at,marketplace_is_sold,posting_type,' +
  'extracted_rent_pln:rent_pln,extracted_rooms:rooms,extracted_area_m2:area_m2,' +
  'extracted_city:city,extracted_suburb:suburb,extracted_subdistrict:subdistrict,' +
  'extracted_lat:lat,extracted_lon:lon,geocode_provider,extracted_extras:extras,' +
  'photos,fb_video_url,' +
  'walkability_score,transit_score,green_score,poi_badges,metro_nearest_name,metro_nearest_m,' +
  'total_monthly_cost_pln,origin,owner_confirmed,owner_present,market_since,klasa_jakosci,ma_zdjecia,' +
  // Źródło opłaty administracyjnej (migracja agenta danych 20260928090404): decyduje o
  // „ok." przy kwocie na karcie i o tym, czy oferta wchodzi do filtra kosztu (SRCH-15).
  'admin_fee_source,' +
  // Wyróżniki (migracja 20260930114353). Tabela, ranking i wielokąt zwracają je od tej migracji;
  // front z nimi wypchnięty PRZED migracją zgasiłby SERP (42703) — ta sama pułapka co
  // `market_since` niżej.
  'po_remoncie,mediana_kosztu_okolicy_pln,ofert_w_porownaniu,taniej_o_procent,' +
  // Obniżka ceny (migracja 20261007081536): wyróżnik „Cena obniżona”. Ta sama pułapka 42703 —
  // front i migracja idą jednym commitem, migracja wchodzi przed frontem.
  'rent_reduced_from_pln,rent_reduced_at';
// `klasa_jakosci` NIE JEST RYSOWANA na karcie — jest tu, bo `applySerpSort` po niej sortuje,
// a na ścieżce wielokąta PostgREST rozwiązuje ORDER BY po WYBRANYCH kolumnach (opis niżej).
// Ten sam powód co przy `posted_at` w PIN_COLUMNS. Pilnuje tego test w serpQuery.test.ts.
// `market_since` („na rynku od N dni") DOSZŁO PO MIGRACJI 20260927132756 i kolejność była
// tu istotna: PostgREST na nieznaną kolumnę odpowiada błędem 42703 i wywraca CAŁY odczyt
// SERP-u, więc front z tą kolumną wypchnięty przed migracją zgasiłby wyszukiwarkę.

/**
 * Map pins. `posted_at` is not rendered — it is here because useSerpPins ORDERS
 * by it, and on the polygon path (flat.offers_in_polygon) PostgREST resolves
 * ORDER BY against the SELECTED record, not the function's full row type:
 *
 *   ?select=id,…&order=posted_at.desc  ->  42703 column record.posted_at does not exist
 *
 * Filters are the opposite — they apply to the function call, so they reach any
 * column (by its REAL name) whether selected or not. So the rule for this file
 * is one-directional and easy to trip over: **every column applySerpSort can
 * order by must appear in the select list**; filter columns need not. Guarded by
 * serpQuery.test.ts so a future sort key can't silently 400 the map.
 */
export const PIN_COLUMNS =
  'id,extracted_lat:lat,extracted_lon:lon,geocode_provider,' +
  // `klasa_jakosci` jedzie z pinem, bo to po niej mapa rozstrzyga, która pigułka przetrwa
  // kolizję (krok O) — a przy okazji da się ją policzyć w pomiarze.
  // ODCHUDZONE 30.09: bez `admin_fee_source` (nic go z pinu nie czyta — karta ma go z
  // SERP_COLUMNS) i bez `posted_at`, `ma_zdjecia` (sortowanie; tabela sortuje po kolumnie bez
  // wybierania jej — dokłada je `kolumnyPinow` tylko na ścieżkach funkcji). Strona 1 000 pinów:
  // 49,6 → 42,2 KB gzip (353 → 265 KB surowo), zmierzone scripts/rest.sh.
  'extracted_suburb:suburb,extracted_rent_pln:rent_pln,klasa_jakosci,' +
  // Tryb kosztu: pigułka pokazuje wtedy koszt, a nie czynsz — więc pin musi go nieść.
  // Powód niepewnej ceny czytamy ŚCIEŻKĄ W JSON-ie, nie kolumną: na `flat.offers` nie ma
  // kolumny `cena_niepewna` (sprawdzone w katalogu — jest `price_uncertain`, świadomie bez
  // grantu dla frontu, DEC-05), a `cena_niepewna` istnieje tylko w typach zwracanych przez
  // funkcje. Wybranie jej tutaj wywaliłoby ścieżkę tabelową na 42703.
  // Po co w ogóle: ścieżka narysowanego obszaru NIE MA sufitu 50 000 zł, który ma
  // `offers_matching`, więc bez tej flagi mapa pokazałaby „1 450 000 zł" przy czterech
  // ofertach z ceną sprzedaży wziętą za najem (zgłoszenie agenta danych, 28.09).
  'total_monthly_cost_pln,niepewny_powod:extras->>price_uncertain_reason';

/**
 * Klucze sortowania pinów, których pin sam nie czyta. Na ŚCIEŻKACH FUNKCJI (`offers_ranked`,
 * `offers_in_polygon`) PostgREST sortuje po WYBRANYCH kolumnach — bez nich 42703 i pusta mapa;
 * na tabeli sortuje po kolumnie tabeli, więc tam ich nie wybieramy.
 */
export const KOLUMNY_SORTOWANIA_PINOW = ',posted_at,ma_zdjecia';

/** Kolumny pinów dla ścieżki odczytu — tabela bez kluczy samego sortowania. */
export function kolumnyPinow(sciezka: SciezkaOdczytu): string {
  return sciezka === 'tabela' ? PIN_COLUMNS : PIN_COLUMNS + KOLUMNY_SORTOWANIA_PINOW;
}

/** Structural subset of PostgrestFilterBuilder — lets tests pass a recording fake. */
export interface SerpFilterableQuery {
  eq(col: string, v: unknown): this;
  in(col: string, v: readonly unknown[]): this;
  gte(col: string, v: unknown): this;
  gt(col: string, v: unknown): this;
  lte(col: string, v: unknown): this;
  contains(col: string, v: readonly unknown[]): this;
  containedBy(col: string, v: readonly unknown[]): this;
  is(col: string, v: unknown): this;
  not(col: string, op: string, v: unknown): this;
  or(filter: string): this;
  order(col: string, opts?: { ascending?: boolean; nullsFirst?: boolean }): this;
}

/**
 * Narysowany obszar: SERP czyta wtedy przez `flat.offers_in_polygon` (jawna lista kolumn w
 * RETURNS TABLE), a nie z tabeli. Jedno miejsce decyzji, bo filtr i odczyt muszą się
 * zgadzać co do ścieżki — kolumna spoza tamtej listy to 42703 i zgaszony obszar.
 */
export function hasPolygon(filters: Pick<SourcePostFilters, 'polygon'>): boolean {
  return Array.isArray(filters.polygon) && filters.polygon.length >= 3;
}

/**
 * TRZY ŚCIEŻKI ODCZYTU SERP-u (1b cz. 2). Jedno miejsce decyzji — lista, licznik i piny muszą
 * iść tą samą ścieżką, bo inaczej licznik i lista opisywałyby różne zbiory.
 *  - `ranking` — aktywny jakikolwiek filtr miękki: `flat.offers_ranked(p_prefs, p_geojson)`.
 *    Zwraca prawdziwe kolumny (furnishing, floor, has_elevator…) i `dopasowanie_klucz`, więc
 *    predykaty idą po kolumnach także w narysowanym obszarze, a kolejność „spełnia wyżej, nie
 *    wiadomo niżej" daje sortowanie po kluczu;
 *  - `obszar` — narysowany wielokąt bez filtrów miękkich: `flat.offers_in_polygon`;
 *  - `tabela` — reszta: `flat.offers` wprost.
 */
export type SciezkaOdczytu = 'tabela' | 'obszar' | 'ranking';

export function sciezkaOdczytu(filters: SourcePostFilters): SciezkaOdczytu {
  if (maMiekkieFiltry(filters)) return 'ranking';
  return hasPolygon(filters) ? 'obszar' : 'tabela';
}

/**
 * Kolumny, które dokłada ścieżka rankingu: klucz (po nim sortujemy — a na ścieżce funkcji
 * PostgREST sortuje po WYBRANYCH kolumnach) i dwie kolumny do adnotacji karty, których
 * `extras` nie ma albo ma inaczej (`has_elevator`: 1 632 rozjazdów, pomiar 28.09).
 */
export const KOLUMNY_RANKINGU = ',dopasowanie_klucz,has_elevator,deposit_pln,amenities_norm';

/** Price column for the active cost mode — 'total' = rent + known utilities
 * (serving-layer column, semantics of src/lib/offerCosts.ts). */
export function costColumn(costMode?: 'rent' | 'total'): string {
  return costMode === 'total' ? 'total_monthly_cost_pln' : 'rent_pln';
}

/**
 * PostgREST or() clause for the rooms buttons (1/2/3 exact, 4 = "4+").
 * Null when the selection doesn't constrain anything.
 */
export function buildRoomsClause(rooms?: number[]): string | null {
  if (!rooms?.length) return null;
  const exact = [...new Set(rooms.filter((r) => r >= 1 && r < 4))].sort();
  const fourPlus = rooms.includes(4);
  const parts: string[] = [];
  if (exact.length) parts.push(`rooms.in.(${exact.join(',')})`);
  if (fourPlus) parts.push('rooms.gte.4');
  return parts.length ? parts.join(',') : null;
}

const POI_SCORE_COLUMN: Record<'transit' | 'walk' | 'green', string> = {
  transit: 'transit_score',
  walk: 'walkability_score',
  green: 'green_score',
};

/**
 * Kwadrat ±`radiusM` wokół punktu (stacja metra, miejsce „Blisko do…”). 1° lat ≈ 111.32 km;
 * 1° lon kurczy się o cos(lat). Granice WCHODZĄ (`gte`/`lte`), więc oferta dokładnie na krawędzi
 * zostaje; rogi kwadratu sięgają ~1,41 × promień.
 */
export function bboxPromienia(p: { lat: number; lon: number; radiusM: number }) {
  const dLat = p.radiusM / 111_320;
  const dLon = p.radiusM / (111_320 * Math.cos((p.lat * Math.PI) / 180));
  return { south: p.lat - dLat, north: p.lat + dLat, west: p.lon - dLon, east: p.lon + dLon };
}

/** Ten sam predykat co `buildAreasOrClause` po stronie bazy, do testów i do klienta. */
export function wPromieniu(p: { lat: number; lon: number; radiusM: number }, lat: number, lon: number): boolean {
  const b = bboxPromienia(p);
  const r = (n: number) => Number(n.toFixed(6));
  return lat >= r(b.south) && lat <= r(b.north) && lon >= r(b.west) && lon <= r(b.east);
}

/**
 * One PostgREST or() across the area chips:
 *  - district chips match by suburb name; the Warsaw gate (LLM tags satellite
 *    towns with Warsaw district vocabulary; NULL city counts as Warsaw) sits
 *    INSIDE the group so it can't contradict a sibling city chip;
 *  - city chips match `city` (other supported cities; per-city district
 *    scoping is ready here whenever their scrape coverage lands);
 *  - estate chips match their bbox over geocoded coords (~1/3 of offers carry
 *    coords — communicated in the picker UI);
 *  - metro-station and „Blisko do…” place chips match ±radius around their point.
 * Null when no area constrains anything.
 */
export function buildAreasOrClause(areas?: SearchArea[]): string | null {
  if (!areas?.length) return null;
  const parts: string[] = [];
  const districts = areas.filter((a) => a.kind === 'district').map((a) => a.name);
  if (districts.length) {
    // PostgREST in.() needs double quotes around values with spaces/diacritics.
    parts.push(
      `and(suburb.in.(${districts.map((d) => `"${d}"`).join(',')}),` +
        'or(city.is.null,city.eq.Warszawa))',
    );
  }
  // Osiedle chips (MSI rejony) match by name on `subdistrict` — the same value
  // the offer card shows — so picker and labels are one system. Warsaw-gated
  // like districts (NULL city counts as Warsaw).
  const subdistricts = areas.filter((a) => a.kind === 'subdistrict').map((a) => a.name);
  if (subdistricts.length) {
    parts.push(
      `and(subdistrict.in.(${subdistricts.map((d) => `"${d}"`).join(',')}),` +
        'or(city.is.null,city.eq.Warszawa))',
    );
  }
  for (const a of areas) {
    if (a.kind === 'city') parts.push(`city.eq."${a.name}"`);
    if (a.kind === 'station' || a.kind === 'place') {
      // Proximity = a bbox of ±radius around the point (offers carry lat/lon,
      // not geog, on this REST path; a square circumscribes the true circle, so
      // it's inclusive). A „Blisko do…” place uses the metro chip's path as is
      // (plan E4.2).
      const b = bboxPromienia(a);
      parts.push(
        `and(lat.gte.${b.south.toFixed(6)},lat.lte.${b.north.toFixed(6)},` +
          `lon.gte.${b.west.toFixed(6)},lon.lte.${b.east.toFixed(6)})`,
      );
    }
    if (a.kind !== 'geo') continue;
    parts.push(
      `and(lat.gte.${a.south},lat.lte.${a.north},` +
        `lon.gte.${a.west},lon.lte.${a.east})`,
    );
  }
  return parts.length ? parts.join(',') : null;
}

// SERP grace window: hide just-scraped posts from the LIST until the dedup
// pipeline (photo download → phash → cross-post merge, ~15-25 min) has had a
// chance to collapse transient cross-post duplicates. Fail-open: anything older
// than the window always shows, even if not fully deduped — we never permanently
// hide inventory. Direct offer-page access (useSerpHead, by id) is NOT gated, so
// a fresh listing is still reachable by URL during the window.
export const SERP_GRACE_MS = 20 * 60_000;

// Data-quality sanity bounds. Offers whose extracted area/rent fall outside these
// are almost certainly typos (a 6798 m² flat; a sale price 3 500 000 zł captured
// as rent). Real Warsaw rentals top out ~337 m² / ~10 000 zł, then there's a
// clean gap to the garbage — so these guards sit safely in that gap and never
// clip a real offer. applySerpFilters hides them from the SERP (results + slider
// scale); they stay in the DB for the backoffice moderation queue.
export const AREA_SANITY_MIN = 8;
export const AREA_SANITY_MAX = 500;
export const RENT_SANITY_MAX = 50_000;

export function applySerpFilters<Q extends SerpFilterableQuery>(
  q: Q,
  filters: SourcePostFilters,
  opcje?: {
    /**
     * Pomija regułę SRCH-15 (w trybie kosztu wypadają oferty bez informacji o opłatach).
     * Służy do JEDNEJ rzeczy: policzenia, ile ofert ta reguła zabrała, żeby powiedzieć to
     * przy liczniku. Nigdzie indziej się jej nie pomija.
     */
    pomijajRegulaOplat?: boolean;
  },
): Q {
  // The serving layer is whole-apartment-only by construction, but the SERP
  // states its own contract rather than inheriting the projection's.
  q = q.eq('post_kind', 'offer_whole_apartment');
  // „Pokaż oferty bez zdjęć” wyłączone — filtr twardy na wszystkich trzech ścieżkach (tabela,
  // ranking, wielokąt zwracają `ma_zdjecia` od migracji 20260929175239).
  if (filters.bezZdjec === false) q = q.eq('ma_zdjecia', true);
  // Pastylka „bez adresu” nad mapą: oferty bez punktu (V1 29.09).
  if (filters.bezAdresu) q = q.is('lat', null);
  // Tombstones are readable by their OWNER (offers_owner_read_own), so RLS
  // alone doesn't hide them: without this, a logged-in owner would find their
  // own expired offer in the SERP. anon is already covered by
  // offers_public_read (status='active'); this makes it true for everyone.
  q = q.eq('status', 'active');
  // Grace window applies to SCRAPED rows only (it exists for the dedup
  // pipeline, which owner rows never enter) — an owner listing must be
  // searchable the moment it's published, not 20 minutes later. For scraped
  // rows the origin disjunct is false, so the predicate is equivalent to the
  // old plain lte(). Serving-layer origins are 'scraped' | 'claimed' |
  // 'owner_only'; both non-scraped ones are owner-driven.
  q = q.or(
    `scraped_at.lte.${new Date(Date.now() - SERP_GRACE_MS).toISOString()},origin.neq.scraped`,
  );
  // Default scope is the CITY OF WARSAW. The product is Warsaw-only and the
  // bar's "Warszawa" chip is the visible expression of this; without this gate
  // the unfiltered SERP returned satellite-town posts too (Otwock, Józefów,
  // Ożarów Mazowiecki…). Those carry their real `city`, so the gate drops
  // them; NULL city counts as Warsaw (most FB posts don't restate it).
  // Skipped only when an explicit `city` chip broadens scope to another
  // supported city — buildAreasOrClause then owns the city predicate (a global
  // AND-gate here would contradict it and return nothing).
  const hasCityChip = filters.areas?.some((a) => a.kind === 'city') ?? false;
  if (!hasCityChip) {
    q = q.or('city.is.null,city.eq.Warszawa');
  }
  // Liveness: hide confirmed-dead (stale_at set by the liveness checker — every
  // value in the set is in the past, so it's a "dead" marker, not a TTL) and
  // marketplace-sold offers. `not.is.true` (NOT eq.false) so FB-only rows whose
  // marketplace_is_sold is NULL stay visible. Skipped for the dev "include dead"
  // lens; never applied to useSerpHead (direct offer links must still resolve).
  if (!filters.includeDead) {
    q = q.is('stale_at', null);
    q = q.not('marketplace_is_sold', 'is', true);
  }
  // Data-quality guard: hide typo outliers (see *_SANITY_* consts) so a 6798 m²
  // flat or a sale-price-as-rent never clutters the SERP. NULL = unknown → kept.
  // The rows stay in the DB for backoffice moderation. The dev `sanityOutliers`
  // lens INVERTS this: it RESTRICTS to exactly those outliers, so they can be
  // reviewed from the SERP until the backoffice moderation queue exists.
  if (filters.sanityOutliers) {
    q = q.or(
      `area_m2.gt.${AREA_SANITY_MAX},area_m2.lt.${AREA_SANITY_MIN},` +
        `rent_pln.gt.${RENT_SANITY_MAX}`,
    );
  } else {
    q = q.or(`area_m2.is.null,and(area_m2.gte.${AREA_SANITY_MIN},area_m2.lte.${AREA_SANITY_MAX})`);
    q = q.or(`rent_pln.is.null,rent_pln.lte.${RENT_SANITY_MAX}`);
  }
  // Dev lenses for data-quality auditing — each RESTRICTS the result to the
  // problem set: offers missing a price, or without exact coords (rendered at
  // the district centroid on the map, never an exact pin).
  if (filters.noPrice) q = q.is('rent_pln', null);
  // "Centroid dzielnicy" lens = the worst case only: no point at all, rendered at
  // the district centroid (jittered). The approximate-but-LOCATED tiers (landmark /
  // postal / osiedle / geo-feature) carry real coords and render as a muted circle
  // on the map — they are NOT this lens (it would contradict the "centroid dzielnicy"
  // label). Map-side exact-vs-approximate styling lives in MapView/geocodePrecision.
  if (filters.approxLocation) q = q.is('lat', null);
  // Agencji nie ma nigdzie: stały warunek, niezależny od `filters.postingTypes` (./bezAgencji.ts).
  q = q.in('posting_type', RODZAJE_BEZ_AGENCJI);
  if (filters.suburbs?.length) {
    // The dropdown lists WARSAW districts; the satellite-town-tagged-with-Warsaw-
    // vocabulary case (a Zielonka flat the LLM labelled "Śródmieście") is already
    // covered by the default Warsaw scope gate above, so no extra city clause
    // is needed here.
    q = q.in('suburb', filters.suburbs);
  }
  const areasClause = buildAreasOrClause(filters.areas);
  if (areasClause) q = q.or(areasClause);
  const priceCol = costColumn(filters.costMode);
  /**
   * CENA NIEPEWNA ZACHOWUJE SIĘ JAK BRAK CENY, gdy filtr ceny jest włączony.
   *
   * Karta takiej oferty nie pokazuje kwoty („Cena do potwierdzenia", DEC-05), ale `rent_pln`
   * zostaje w bazie — więc bez tego warunku filtr „do 2 000 zł" dopasowywał ofertę po kwocie,
   * której człowiek nigdzie nie widzi, i wstawiał w wyniki kartę bez ceny. Reguła agenta
   * danych z 27.09 (`rent_per_m2_far_below_segment`) robi z tego przypadek realny: 8 ofert
   * z kwotami 1 210–2 000 zł, czyli w KAŻDYM zakresie od 2 000 zł w górę, a w wąskim
   * wyszukiwaniu („Ursynów do 2 000 zł") to 4 z 8 wyników.
   *
   * Bez filtra ceny oferta zostaje widoczna — niepewna cena nie jest powodem do ukrywania
   * mieszkania, tylko do niepokazywania kwoty.
   *
   * KOSZT, świadomie przyjęty: razem z nimi z najtańszego pasa wypada jedna oferta, która
   * naprawdę jest tania (2 000 zł za 56 m² na Mokotowie). Nie umiemy jej odróżnić od pomyłki
   * w ogłoszeniu, a pokazanie kwoty, której nie potwierdziliśmy, kosztuje więcej.
   *
   * `extras` jest w grancie dla anona (SERP_COLUMNS wybiera `extracted_extras:extras`), więc
   * ten warunek nie potrzebuje kolumny `price_uncertain`, która grantu nie ma.
   */
  const filtrCeny = typeof filters.rentMin === 'number' || typeof filters.rentMax === 'number';
  if (filtrCeny) q = q.is('extras->>price_uncertain_reason', null);
  /**
   * SRCH-15: W TRYBIE KOSZTU OFERTA BEZ INFORMACJI O OPŁATACH WYPADA (decyzja Piotra 28.09).
   *
   * Obietnicą filtra „koszt do 4 000 zł" jest „na pewno nie więcej niż 4 000". Przy
   * nieznanych opłatach koszt w bazie równa się samemu czynszowi, czyli liczymy opłaty jako
   * ZERO — i obiecujemy coś, czego nie wiemy, zawsze w jedną stronę: oferta wygląda taniej,
   * niż jest. `estimated` zostaje (kwota jest, tylko z szacunku — karta mówi „ok."),
   * wypada wyłącznie `unknown`.
   *
   * Liczby agenta danych z 28.09: reguła obejmuje 348 ofert, ale realnie zmienia widok o
   * 157 — reszta i tak nie ma czynszu i wypadała z filtra ceny z innego powodu.
   *
   * W trybie „Cena najmu" NIE ROBIMY TEGO: tam pytamy o sam czynsz, a ten jest znany.
   */
  // Wiersz z `admin_fee_source = NULL` (projekcja jeszcze go nie dotknęła) też wypada i to
  // jest właściwy kierunek: nie znamy źródła, więc nie obiecujemy. Dziś takich nie ma
  // (sprawdzone w katalogu 28.09: provided 5 444, estimated 1 306, unknown 564, NULL 0).
  if (filtrCeny && filters.costMode === 'total' && !filters.nieznaneOplaty && !opcje?.pomijajRegulaOplat) {
    q = q.not('admin_fee_source', 'eq', 'unknown');
  }
  if (typeof filters.rentMin === 'number') q = q.gte(priceCol, filters.rentMin);
  if (typeof filters.rentMax === 'number') q = q.lte(priceCol, filters.rentMax);
  const roomsClause = buildRoomsClause(filters.rooms);
  if (roomsClause) q = q.or(roomsClause);
  if (typeof filters.areaMin === 'number') q = q.gte('area_m2', filters.areaMin);
  if (typeof filters.areaMax === 'number') q = q.lte('area_m2', filters.areaMax);
  if (filters.nearMetro) q = q.lte('metro_nearest_m', 800);
  for (const p of filters.poi ?? []) q = q.gte(POI_SCORE_COLUMN[p], 70);
  /**
   * SZUKAM ZE ZWIERZĘCIEM — odcinamy JAWNE „nie", resztę zostawiamy (zwierzęta miękko,
   * decyzja Piotra 28.09). Twarde „tylko z deklaracją tak" ukrywało 90% rynku: 627 z 6 190
   * ofert widocznych w domyślnym widoku (pomiar agenta danych 28.09). Oferta bez
   * deklaracji zostaje, a karta mówi „zapytaj właściciela".
   *
   * NULL MUSI PRZEJŚĆ — i to jest błąd, który tu był: `not('extras->>pets_allowed','eq',
   * 'false')` to w SQL `NOT (x = 'false')`, a dla braku deklaracji daje NULL, czyli wiersz
   * odpada. Zmierzone 28.09 na aktywnych: 743 zamiast 6 115 — filtr „miękki" był twardy.
   * `IS NOT FALSE` jest dwuwartościowe: true i NULL przechodzą, false nie.
   *
   * DWIE ŚCIEŻKI, JEDNA REGUŁA. Tabela czyta kolumnę `pets_allowed` (grant od migracji
   * 20260928094242, 0 rozjazdów z `extras` na populacji). `flat.offers_in_polygon` tej
   * kolumny NIE zwraca (jawna lista RETURNS TABLE) — filtr po niej dałby 42703 i zgasił
   * narysowany obszar, więc tam ten sam warunek idzie przez `extras`. Oba warianty dają
   * 6 115 na aktywnych (zmierzone 28.09).
   */
  // Zwierzę jest filtrem miękkim, więc z nim SERP czyta zawsze przez `offers_ranked`, która
  // zwraca kolumnę `pets_allowed` — także w narysowanym obszarze.
  if (filters.zeZwierzeciem) q = q.not('pets_allowed', 'is', false);
  // FILTRY MIĘKKIE etapu 1b: jawne „nie spełnia" wypada, brak danych zostaje — po kolumnach
  // (src/lib/filtryMiekkie.ts), bo ścieżką jest wtedy zawsze `offers_ranked`.
  for (const warunek of predykatyWykluczenia(filters)) q = q.or(warunek);
  // „Od zaraz" — wolne TERAZ, czyli dokładnie to, co karta pokazuje pastylką.
  //
  // Para z `src/lib/availability.ts` rozstrzyga to samo po stronie klienta i ta reguła MUSI
  // czytać ją tak samo, bo inaczej filtr wyrzuciłby oferty z własną pastylką „od zaraz":
  // gdy jest DATA, wygrywa data (przeszła = wolne, przyszła = nie), a flaga liczy się tylko
  // wtedy, gdy daty nie ma. Dlatego to dwa warunki w `or`, a nie jeden `eq` na fladze.
  // „Właściciel na FlatHero” (A5, §1.16): każdy właściciel na platformie — `origin <> 'scraped'`,
  // ta sama reguła co `owner_on_platform` w `offers_matching` (histogram). `origin` jest w wyniku
  // wszystkich trzech ścieżek (tabela, `offers_ranked`, `offers_in_polygon`).
  if (filters.wlascicielNaFlatHero) q = q.not('origin', 'eq', 'scraped');
  // „Ulubione” (A6): tylko oferty z listy oglądającego. Pusta lista daje zero wierszy — `in.()`
  // bez elementów PostgREST odrzuca, więc idzie identyfikator, którego żadna oferta nie ma.
  if (filters.ulubione) q = q.in('id', filters.ulubioneIds?.length ? filters.ulubioneIds : [BRAK_ULUBIONYCH]);
  if (filters.availableNow) {
    const dzis = dzisiejszaDataLokalna();
    q = q.or(
      `and(extras->>available_from.not.is.null,extras->>available_from.lte.${dzis}),` +
        'and(extras->>available_from.is.null,extras->>available_immediately.eq.true)',
    );
  }
  if (filters.postedSinceHours) {
    const cutoff = new Date(Date.now() - filters.postedSinceHours * 3600_000).toISOString();
    q = q.gte('posted_at', cutoff);
  }
  // FILTRA PLATFORM I SOCZEWKI „Tylko FB" JUŻ NIE MA (DEC-04, 28.09): filtrowały po kolumnie
  // `platforms`, którą baza odbiera wszystkim rolom publicznym — `contains` na kolumnie bez
  // uprawnień to 42501 i zgaszona wyszukiwarka, nie pusty filtr. Wgląd w portale jest w /admin.
  return q;
}

/**
 * KOLEJNOŚĆ PINÓW NA MAPIE — osobna od listy, bo mapa nie ma sortowania wybieranego przez
 * człowieka: priorytetem jest klasa jakości (krok O), potem świeżość.
 *
 * `id` na końcu NIE JEST OZDOBĄ. Zapytanie o piny chodzi stronami po 1000 (`.range`), a
 * stronicowanie po kluczu, który się powtarza, gubi i dubluje wiersze: dwie oferty z tą samą
 * datą mogą przy kolejnych zapytaniach wypaść po różnych stronach granicy. Ta sama reguła i
 * ten sam powód, co w `applySerpSort`.
 *
 * @param pomijajKlase ścieżka narysowanego obszaru — `flat.offers_in_polygon` nie zwracała
 *        `klasa_jakosci` do czasu migracji 20260927205841, a sortowanie po kolumnie spoza
 *        wyniku funkcji to 42703 i zero ofert.
 */
export function applyPinSort<Q extends SerpFilterableQuery>(q: Q, sciezka: SciezkaOdczytu = 'tabela'): Q {
  // Zdjęcia PIERWSZE, na każdej ścieżce — ta sama kolejność co lista (patrz `applySerpSort`).
  q = q.order('ma_zdjecia', { ascending: false, nullsFirst: false });
  // Ranking: dopasowanie PRZED klasą — ta sama kolejność co lista (SRCH-24 v2, kreska filtrów
  // miękkich); przy sufice 1 000 pinów o tym, kto przetrwa, decyduje najpierw dopasowanie.
  if (sciezka === 'ranking') q = q.order('dopasowanie_klucz', { ascending: true, nullsFirst: false });
  if (sciezka !== 'obszar') q = q.order('klasa_jakosci', { ascending: true, nullsFirst: false });
  return q
    .order('posted_at', { ascending: false, nullsFirst: false })
    .order('id', { ascending: true });
}

/**
 * Server-side ordering. Every branch appends an `id` tiebreak: offset
 * pagination (`.range`) needs a TOTAL order, or rows with equal sort keys
 * drift between pages and get duplicated/skipped.
 */
export function applySerpSort<Q extends SerpFilterableQuery>(
  q: Q,
  sort?: SortKey,
  costMode?: 'rent' | 'total',
  /** `obszar`: `offers_in_polygon` nie zwraca `klasa_jakosci` (hotfix 27.09); `ranking`: sortuj po dopasowaniu. */
  sciezka: SciezkaOdczytu = 'tabela',
): Q {
  const key = sort ?? 'newest';
  const priceCol = costColumn(costMode);
  // KLASA JAKOŚCI PRZED WYBRANYM SORTOWANIEM (krok O, DEC-05, Piotr 27.09). Najpierw oferty
  // ze zdjęciami i ceną, potem te, którym brakuje jednego, na końcu bez obu — a wybrane
  // sortowanie działa WEWNĄTRZ grupy, więc „najtańsze" dalej znaczy najtańsze.
  // Kolumna jest generowana w bazie (migracja 20260927201929): liczenie tego wyrażenia w
  // ORDER BY kosztowało 47,6 ms zamiast 12,6 ms, bo ściągało `photos` i `extras` z TOAST-u.
  // Świeża oferta bez pobranych jeszcze zdjęć spada na czas pobierania niżej i wraca sama —
  // to zamierzone (mediana pobrania zdjęć: 59 min).
  // FILTRY MIĘKKIE: „spełnia wyżej, nie wiadomo niżej” — od SRCH-24 v2 PRZED klasą jakości
  // (Maciek/Piotr 29.09: kreska na liście „tu kończą się oferty potwierdzające wszystko”). Przy
  // kolejności klasa → dopasowanie oferta z „nie wiadomo” i zdjęciami stała nad potwierdzoną bez
  // zdjęć, więc jednej kreski nie dało się postawić. Nad kreską (klucz 0) klasa dalej decyduje.
  // `dopasowanie_klucz` = (maska niedopasowań << 7)
  // Kontrakt (comment on function flat.offers_ranked): klucz = (niedopasowane_bity * 128 +
  // nieznane_bity) * 16 + min(brakujące udogodnienia, 15); bity ważone priorytetem pola.
  // Niedopasowania i tak wycina łańcuch filtrów, więc decydują nieznane — mniej i mniej ważne
  // wyżej — a udogodnienia rozstrzygają tylko remisy.
  // OFERTY BEZ ZDJĘĆ NA KONIEC — pierwszy klucz, na każdej ścieżce (decyzja Piotra 29.09).
  // Układ listy: ze zdjęciem i potwierdzone → kreska miękka → ze zdjęciem z „nie wiadomo”
  // → druga kreska → WSZYSTKIE bez zdjęć (w środku dalej dopasowanie i klasa). Musi to robić
  // serwer: sortowanie po stronie przeglądarki przy doładowaniu strony wpuściłoby ofertę ze
  // zdjęciem pod drugą kreskę. `klasa_jakosci` tego nie załatwiała: klasa 2 to „zdjęcia bez
  // ceny” ALBO „cena bez zdjęć” (664 + 469 ofert 29.09), przemieszane po dacie.
  q = q.order('ma_zdjecia', { ascending: false, nullsFirst: false });
  if (sciezka === 'ranking') q = q.order('dopasowanie_klucz', { ascending: true, nullsFirst: false });
  if (sciezka !== 'obszar') q = q.order('klasa_jakosci', { ascending: true, nullsFirst: false });
  if (key === 'oldest') q = q.order('posted_at', { ascending: true, nullsFirst: false });
  else if (key === 'cheapest') q = q.order(priceCol, { ascending: true, nullsFirst: false });
  else if (key === 'expensive') q = q.order(priceCol, { ascending: false, nullsFirst: false });
  else {
    // Default ("recommended") view PREMIERS supply where the owner is present on
    // FlatHero — the product favours it over passive scrapes (Piotr 2026-07-23).
    // Trust ladder, each tier then by recency:
    //   1. VERIFIED owner (owner_confirmed=true — ownership AND identity, manual
    //      OR claimed) → strongest signal, ranked first;
    //   2. owner on FlatHero (owner_present=true — manual listing OR claimed
    //      scraped offer, not yet verified) → next.
    // owner_present subsumes the old origin tiebreak (manual is always present,
    // and a claimed scrape now is too). Explicit price/oldest sorts stay literal
    // (no premiering) — a chosen sort must be honoured.
    q = q.order('owner_confirmed', { ascending: false, nullsFirst: false });
    q = q.order('owner_present', { ascending: false, nullsFirst: false });
    q = q.order('posted_at', { ascending: false, nullsFirst: false });
  }
  return q.order('id', { ascending: true });
}

/**
 * SEKCJE LISTY przy filtrach miękkich (Piotr 29.09) — warunki, które dzielą wynik BEZ RESZTY:
 *  • `nadKreska`     — ze zdjęciem i potwierdza wszystko (`dopasowanie_klucz = 0`);
 *  • `podKreska`     — ze zdjęciem, z „nie wiadomo” (`> 0`) — to jest N w „Pozostałe N ofert”;
 *  • `bezZdjec`      — wszystkie bez zdjęć, niezależnie od dopasowania (za drugą kreską);
 *  • `potwierdzone`  — potwierdza wszystko, ze zdjęciem albo bez — N w stopce panelu „N z M”.
 * Dokładane do tego samego łańcucha co lista (`applySerpFilters` na ścieżce rankingu).
 */
export type SekcjaListy = 'nadKreska' | 'podKreska' | 'bezZdjec' | 'potwierdzone';

export function applySekcja<Q extends SerpFilterableQuery>(q: Q, sekcja: SekcjaListy): Q {
  if (sekcja === 'nadKreska') return q.eq('ma_zdjecia', true).eq('dopasowanie_klucz', 0);
  if (sekcja === 'podKreska') return q.eq('ma_zdjecia', true).gt('dopasowanie_klucz', 0);
  if (sekcja === 'bezZdjec') return q.eq('ma_zdjecia', false);
  return q.eq('dopasowanie_klucz', 0);
}
